import React, {
    useEffect,
    useMemo,
    useState,
    createContext,
    useContext,
} from 'react'
import { JsonForms } from '@jsonforms/react'
import $RefParser from '@apidevtools/json-schema-ref-parser'
import { useTheme, StyledEngineProvider, Typography, Box } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { materialCells, materialRenderers } from '@jsonforms/material-renderers'
import { createAjv, isEnumControl } from '@jsonforms/core'
import { get, merge } from 'lodash-es'

import CustomEnumControl from './controls/CustomEnumControl'
import CustomEnumControlTester from './controls/CustomEnumControlTester'
import CustomEnumCell from './controls/CustomEnumCell'
import CustomLabel from './controls/CustomLabel'
import CustomLabelTester from './controls/CustomLabelTester'
import Divider from './controls/Divider'
import DividerTester from './controls/DividerTester'
import MultipleSelectFieldControl from './controls/MultipleSelectFieldControl'
import MultipleSelectFieldControlTester from './controls/MultipleSelectFieldControlTester'
import FileInputControl from './controls/FileInputControl'
import FileInputControlTester from './controls/FileInputControlTester'
import SubtitleControl from './controls/Subtitle'
import SubtitleControlTester from './controls/SubtitleTester'
import DateControl from './controls/DateControl'
import DateControlTester from './controls/DateControlTester'
import SectionLayout, { sectionTester } from './controls/SectionLayout'
import DynamicOneOfControl, {
    dynamicOneOfControlTester,
} from './controls/DynamicOneOfControl'
import DynamicOneOfCellControl from './controls/DynamicOneOfCellControl'
import DependentSelectControl, {
    DependentSelectControlTester,
} from './controls/DependentSelectControl'
import DependentDynamicCellControl from './controls/DependentCellControl'
import LinkWithCopyControl from './controls/LinkWithCopyControl'
import LinkWithCopyControlTester from './controls/LinkWithCopyControlTester'
import TextWithCopyControl from './controls/TextWithCopyControl'
import TextWithCopyControlTester from './controls/TextWithCopyControlTester'

const DynamicConfigContext = createContext()

const DynamicConfigContextProvider = ({ value, children }) => (
    <DynamicConfigContext.Provider value={value}>
        {children}
    </DynamicConfigContext.Provider>
)

export const useDynamicConfigs = () => useContext(DynamicConfigContext)

const PreviewModeContext = createContext(false)

export const usePreviewMode = () => useContext(PreviewModeContext)

// The host's handler for a form whose configuration cannot be rendered
// properly, called with a message. Undefined when the host supplied none.
const ConfigProblemContext = createContext(undefined)

export const useConfigProblem = () => useContext(ConfigProblemContext)

// Merged into the host app's theme, so it holds only what config forms
// decide for themselves.
export const jsonFormTheme = {
    // Read by JSON Forms' text input for the clear button's icon, which
    // otherwise takes the palette's default background.
    jsonforms: {
        input: {
            delete: {
                background: '#ffffff',
            },
        },
    },
}

// The input style, one of MUI's variants: outlined, filled, or standard,
// which is the underlined input. It is set on every input-bearing
// component, so the form keeps one look whatever the host's own inputs
// look like; the hosts spread react-admin's default theme, which makes
// inputs filled.
export const inputVariantTheme = (variant) => ({
    components: {
        MuiFormControl: { defaultProps: { variant } },
        MuiTextField: { defaultProps: { variant } },
        MuiSelect: { defaultProps: { variant } },
    },
})

// Applied when the host asks for full width. A renderer marks its control
// full width unless the uischema trims it, so a trimmed control keeps the
// narrow form's width instead of its own natural one.
export const fullWidthTheme = {
    components: {
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '&:not(.MuiFormControl-fullWidth)': {
                        width: '225px',
                    },
                    // Inside an array table a control fills its cell.
                    '.MuiTableCell-root &': {
                        width: '100%',
                    },
                },
            },
        },
    },
}

// Applied otherwise. One fixed width.
export const fixedWidthTheme = {
    components: {
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: '225px',
                    // Inside an array table a control fills its cell.
                    '.MuiTableCell-root &': {
                        width: '100%',
                    },
                },
            },
        },
    },
}

// TEMPORARY, applied with legacyMarketplace. The marketplace's dropdowns
// were trimmed to at least 225px; its trimmed text inputs had no minimum.
const legacyMarketplaceTheme = {
    components: {
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '&:not(.MuiFormControl-fullWidth):has(.MuiAutocomplete-inputRoot, .MuiSelect-select)': {
                        minWidth: '225px',
                    },
                },
            },
        },
    },
}

// TEMPORARY, used with legacyMarketplace. Dropdowns bound to a root
// property are trimmed wherever the layout puts them, and multi-selects at
// any depth, including array and object details, whatever the uischema or
// fullWidth says. Dropdowns under nested data were stretched to their row,
// so they are left alone, as is the dynamic dropdown, which ignored trim.
const trimLegacyDropdowns = (element, rootSchema, inDetail = false) => {
    if (!element || typeof element !== 'object') {
        return element
    }
    let result = element
    if (Array.isArray(element.elements)) {
        result = {
            ...result,
            elements: element.elements.map((child) =>
                trimLegacyDropdowns(child, rootSchema, inDetail)
            ),
        }
    }
    const detail = get(element, 'options.detail')
    if (detail && typeof detail === 'object') {
        result = {
            ...result,
            options: {
                ...result.options,
                detail: trimLegacyDropdowns(detail, rootSchema, true),
            },
        }
    }
    const rootProperty =
        !inDetail &&
        typeof element.scope === 'string' &&
        element.scope.match(/^#\/properties\/([^/]+)$/)?.[1]
    const context = { rootSchema, config: {} }
    const isDropdown =
        element.type === 'MultiSelectControl' ||
        (rootProperty &&
            !get(rootSchema, ['properties', rootProperty, 'dynamicOneOfKey']) &&
            (isEnumControl(element, rootSchema, context) ||
                DependentSelectControlTester(element, rootSchema, context) > 0))
    return isDropdown
        ? { ...result, options: { ...result.options, trim: true } }
        : result
}

// Shown in place of the form when it cannot render. The people editing a
// config form are the ones who can fix it, so the message is the failure
// itself.
const FormError = ({ heading, message }) => (
    <Box
        sx={{
            padding: '10px',
            background: '#f5f5f5',
            width: '75%',
        }}
    >
        <Typography>{heading}</Typography>
        <Typography color="error">{message}</Typography>
    </Box>
)

// In React 18, error boundaries are still class components.
export class JsonFormsErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }

    // React only logs uncaught render errors in development builds, so the
    // boundary logs on its own to leave a trace in production.
    componentDidCatch(error, info) {
        console.error(
            'JsonFormsWrapper: a renderer threw while rendering the form',
            error,
            info.componentStack
        )
    }

    render() {
        if (this.state.hasError) {
            return (
                <FormError
                    heading="An error occurred while rendering the form:"
                    message={this.state.error?.message}
                />
            )
        }

        return this.props.children
    }
}

// AdvancedMapperControl is not registered here. It pulls in final-form,
// mui-rff, and react-final-form-* deps that cliconfigs should not bundle, so
// pandipackV2 wraps this component and injects it via `extraRenderers`.
// AdvancedMapper is deprecated and stays in pandipackV2, maintained only for
// existing users; cliconfigs cannot render `AdvancedMapper`-typed fields and
// that gap is accepted.
export const JsonFormsWrapper = ({
    schema,
    uischema,
    onJsonFormChange,
    data = {},
    readonly = false,
    fullWidth,
    inputVariant = 'outlined',
    legacyMarketplace = false,
    dynamicConfigs = {},
    previewMode = false,
    onConfigProblem = undefined,
    extraRenderers = [],
    extraCells = [],
}) => {
    const updateData = (errors, data) => {
        onJsonFormChange(errors, data)
    }

    // Resolves refs, which the advanced mapper needs, on a copy: the
    // annotations below never reach the document the host passed in.
    const [resolvedSchema, setSchema] = useState()
    useEffect(() => {
        $RefParser
            .dereference(schema, { mutateInputSchema: false })
            .then((res) => setSchema(res))
    }, [schema])

    const ajv = createAjv({ useDefaults: true, coerceTypes: true })

    // custom AJV keyword `requireNotEmpty` rejects blank string inputs
    // (whitespace-only values fail validation). Auto-attached to all `required`
    // string fields below.
    ajv.addKeyword({
        keyword: 'requireNotEmpty',
        type: 'string',
        validate: function validate(_schema, data) {
            validate.errors = [
                {
                    keyword: 'requireNotEmpty',
                    message: 'Field must not be empty.',
                    params: { keyword: 'requireNotEmpty' },
                },
            ]
            return data.trim() !== ''
        },
        errors: true,
    })

    // Kept outside useEffect so a memoized parent doesn't see a stale schema.
    if (get(resolvedSchema, 'required')) {
        resolvedSchema.required.forEach((item) => {
            const schemaPropItem = get(resolvedSchema, `properties.${item}`)
            if (
                schemaPropItem &&
                schemaPropItem.type === 'string' &&
                get(schemaPropItem, 'requireNotEmpty') === undefined
            ) {
                merge(resolvedSchema.properties[item], {
                    requireNotEmpty: true,
                })
            }
        })
    }

    // TEMPORARY. The integration marketplace keeps the form's earlier look
    // until its own change lands: dropdowns are trimmed to a minimum width,
    // top-level string controls are trimmed unless the host asks for full
    // width, and every other control takes its renderer's own width. Remove
    // this block, legacyMarketplaceTheme, trimLegacyDropdowns, the
    // legacyMarketplace prop, and the prop at the marketplace's call sites
    // together.
    const renderedUischema = useMemo(() => {
        if (!legacyMarketplace || !Array.isArray(uischema?.elements)) {
            return uischema
        }
        const withDropdowns = trimLegacyDropdowns(uischema, resolvedSchema)
        if (fullWidth) {
            return withDropdowns
        }
        return {
            ...withDropdowns,
            elements: withDropdowns.elements.map((element) => {
                if (
                    element.type !== 'Control' ||
                    typeof element.scope !== 'string' ||
                    get(element, 'options.trim') !== undefined
                ) {
                    return element
                }
                const matchingSchema = get(
                    resolvedSchema,
                    'properties.' + element.scope.split('/')[2]
                )
                // The dynamic dropdown ignored trim then, so it is left alone.
                return get(matchingSchema, 'type') === 'string' &&
                    !get(matchingSchema, 'dynamicOneOfKey')
                    ? {
                          ...element,
                          options: { ...element.options, trim: true },
                      }
                    : element
            }),
        }
    }, [legacyMarketplace, fullWidth, uischema, resolvedSchema])
    const widthTheme = legacyMarketplace
        ? legacyMarketplaceTheme
        : fullWidth
        ? fullWidthTheme
        : fixedWidthTheme

    // createTheme deep-merges its extra arguments, so the host's component
    // defaults and overrides stay in force inside the form, and the form's
    // own settings win where both set one. TEMPORARY: with
    // legacyMarketplace the host's component settings are dropped, as the
    // earlier form did, so the marketplace's fixed control width stays out.
    const mainTheme = useTheme()
    const theme = useMemo(
        () =>
            createTheme(
                legacyMarketplace
                    ? { ...mainTheme, components: {} }
                    : mainTheme,
                jsonFormTheme,
                inputVariantTheme(inputVariant),
                widthTheme
            ),
        [mainTheme, legacyMarketplace, inputVariant, widthTheme]
    )

    return (
        <JsonFormsErrorBoundary>
            {resolvedSchema && (
                <PreviewModeContext.Provider value={previewMode}>
                    <ConfigProblemContext.Provider value={onConfigProblem}>
                        <DynamicConfigContextProvider value={dynamicConfigs}>
                            <StyledEngineProvider injectFirst>
                                <ThemeProvider theme={theme}>
                                    {ajv.validateSchema(resolvedSchema) ? (
                                        <JsonForms
                                            data={data}
                                            schema={resolvedSchema}
                                            uischema={renderedUischema}
                                            readonly={readonly}
                                            validationMode="ValidateAndHide"
                                            renderers={[
                                                ...materialRenderers,
                                                ...extraRenderers,
                                                {
                                                    tester: CustomEnumControlTester,
                                                    renderer: CustomEnumControl,
                                                },
                                                {
                                                    tester: sectionTester,
                                                    renderer: SectionLayout,
                                                },
                                                {
                                                    tester: CustomLabelTester,
                                                    renderer: CustomLabel,
                                                },
                                                {
                                                    tester: DividerTester,
                                                    renderer: Divider,
                                                },
                                                {
                                                    tester: MultipleSelectFieldControlTester,
                                                    renderer: MultipleSelectFieldControl,
                                                },
                                                {
                                                    tester: FileInputControlTester,
                                                    renderer: FileInputControl,
                                                },
                                                {
                                                    tester: SubtitleControlTester,
                                                    renderer: SubtitleControl,
                                                },
                                                {
                                                    tester: DateControlTester,
                                                    renderer: DateControl,
                                                },
                                                {
                                                    tester: dynamicOneOfControlTester,
                                                    renderer: DynamicOneOfControl,
                                                },
                                                {
                                                    tester: LinkWithCopyControlTester,
                                                    renderer: LinkWithCopyControl,
                                                },
                                                {
                                                    tester: TextWithCopyControlTester,
                                                    renderer: TextWithCopyControl,
                                                },
                                                {
                                                    tester: DependentSelectControlTester,
                                                    renderer: DependentSelectControl,
                                                },
                                            ]}
                                            cells={[
                                                ...materialCells,
                                                ...extraCells,
                                                {
                                                    tester: dynamicOneOfControlTester,
                                                    cell: DynamicOneOfCellControl,
                                                },
                                                {
                                                    tester: DependentSelectControlTester,
                                                    cell: DependentDynamicCellControl,
                                                },
                                                {
                                                    tester: CustomEnumControlTester,
                                                    cell: CustomEnumCell,
                                                },
                                            ]}
                                            onChange={({ errors, data }) =>
                                                updateData(errors, data)
                                            }
                                            ajv={ajv}
                                        />
                                    ) : (
                                        <FormError
                                            heading="The config schema is invalid:"
                                            message={ajv.errorsText(ajv.errors)}
                                        />
                                    )}
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </DynamicConfigContextProvider>
                    </ConfigProblemContext.Provider>
                </PreviewModeContext.Provider>
            )}
        </JsonFormsErrorBoundary>
    )
}
